import React, { useCallback, useEffect, useRef } from 'react';
import { getWebpSrc } from '../../utils/imageFormats';
import { getPortfolioImageAlt } from '../../utils/imageAltText';

export const ImageModal = ({ selectedImage, setSelectedImage, filteredImages }) => {
  const modalRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  const hasMultipleImages = filteredImages.length > 1;
  const currentIndex = selectedImage
    ? filteredImages.findIndex((img) => img.id === selectedImage.id)
    : -1;

  const categoryLabel = category => {
    const labels = {
      equine: 'Equine',
      'black-background': 'Black Background',
      couples: 'Couples',
      weddings: 'Weddings',
      family: 'Family',
      senior: 'Senior',
      prom: 'Prom',
      pets: 'Pets'
    };
    return labels[category] || category;
  };

  const navigateImage = useCallback(
    direction => {
      if (!selectedImage) return;
      const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
      let newIndex;

      if (direction === 'next') {
        newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
      } else {
        newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
      }

      setSelectedImage(filteredImages[newIndex]);
    },
    [filteredImages, selectedImage, setSelectedImage]
  );

  useEffect(() => {
    if (!selectedImage) return;

    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);

    if (currentIndex !== -1 && hasMultipleImages) {
      const nextImage = filteredImages[(currentIndex + 1) % filteredImages.length];
      const previousImage = filteredImages[(currentIndex - 1 + filteredImages.length) % filteredImages.length];

      [nextImage, previousImage].forEach(image => {
        if (!image?.src) {
          return;
        }

        const preloadJpeg = new Image();
        preloadJpeg.src = image.src;

        const preloadWebp = new Image();
        preloadWebp.src = getWebpSrc(image.src);
      });
    }

    previouslyFocusedElementRef.current = document.activeElement;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const focusableElements = modalRef.current
      ? Array.from(modalRef.current.querySelectorAll(focusableSelector)).filter(
          element => !element.hasAttribute('disabled')
        )
      : [];

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setSelectedImage(null);
      }

      if (hasMultipleImages && event.key === 'ArrowLeft') {
        navigateImage('prev');
      }

      if (hasMultipleImages && event.key === 'ArrowRight') {
        navigateImage('next');
      }

      if (event.key === 'Tab' && focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);

      if (
        previouslyFocusedElementRef.current &&
        typeof previouslyFocusedElementRef.current.focus === 'function'
      ) {
        previouslyFocusedElementRef.current.focus();
      }
    };
  }, [filteredImages, hasMultipleImages, navigateImage, selectedImage, setSelectedImage]);

  if (!selectedImage) return null;

  const imageTitle = selectedImage.title || getPortfolioImageAlt(selectedImage);
  const imageDescription = selectedImage.description;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-modal-title"
      aria-describedby={imageDescription ? 'image-modal-description' : undefined}
      onClick={event => {
        if (event.target === event.currentTarget) {
          setSelectedImage(null);
        }
      }}
    >
      <div className="relative max-h-full max-w-5xl">
        <button
          type="button"
          onClick={() => setSelectedImage(null)}
          className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 transition-colors hover:bg-black/70"
          aria-label="Close image viewer"
        >
          <svg
            className="h-6 w-6 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {hasMultipleImages && (
          <button
            type="button"
            onClick={() => navigateImage('prev')}
            className="absolute left-4 top-1/2 z-10 -translate-y-1/2 transform rounded-full bg-black/50 p-2 transition-colors hover:bg-black/70"
            aria-label="Show previous image"
          >
            <svg
              className="h-6 w-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <polyline points="15,18 9,12 15,6" />
            </svg>
          </button>
        )}

        {hasMultipleImages && (
          <button
            type="button"
            onClick={() => navigateImage('next')}
            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 transform rounded-full bg-black/50 p-2 transition-colors hover:bg-black/70"
            aria-label="Show next image"
          >
            <svg
              className="h-6 w-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <polyline points="9,18 15,12 9,6" />
            </svg>
          </button>
        )}

        <picture>
          <source srcSet={getWebpSrc(selectedImage.src)} type="image/webp" />
          <img
            src={selectedImage.src}
            alt={getPortfolioImageAlt(selectedImage)}
            decoding="async"
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        </picture>

        <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-black/70 p-6 backdrop-blur-sm">
          <p className="mb-2 inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-100">
            {categoryLabel(selectedImage.category)}
          </p>
          <h2 id="image-modal-title" className="text-lg font-semibold text-white">
            {imageTitle}
          </h2>
          {imageDescription && (
            <p id="image-modal-description" className="mt-1 text-sm text-white/85">
              {imageDescription}
            </p>
          )}
          {hasMultipleImages && currentIndex >= 0 && (
            <p className="mt-2 text-sm text-white/85" aria-live="polite">
              Image {currentIndex + 1} of {filteredImages.length}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
